import { type GetTagSemanticiTipoSezione } from '@framework/api-client/api/users/schemas/getTagSemanticiTipoSezione.ts';
import { type TagSemanticoDTO } from '@framework/api-client/api/users/schemas/tagSemanticoDTO.ts';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';
import { getSemanticTagColumns } from '../getSemanticTagColumn.ts';
import { type ISemanticField } from '../ISemanticField.ts';
import { SemanticTagLogic } from '../SemanticTagLogic.ts';

/**
 * Hook che genera le colonne della tabella in base alla lista dei tag semantici fornita.
 * Utilizzato per creare automaticamente le colonne con i valori dei campi semantici nelle
 * liste quando si fa una ricerca per un tipo specifico (e.g. tipo procedimento, tipo domanda).
 *
 * Tutti i tag semantici "comuni" alle entità in elenco vengono raccolti, mappati nelle colonne
 * e aggiunti così da poter essere visibili, ordinabili e ricercabili.
 */
export function useSemanticTagColumns(options: {
  semanticTags: TagSemanticoDTO[];

  /**
   * Lista di tutti i campi semantici contenuti nelle entità in elenco, utilizzati
   * poi per mostrare i tag semantici nelle tabelle in maniera automatica.
   * e.g. procedures.map(s => s.campiSemantici).flat()
   */
  semanticFields: ISemanticField[];

  /**
   * Filtro utilizzato per filtrare i tag semantici da utilizzare.
   * e.g. Frontoffice sono da vedere solo quelli FO, Backoffice invece entrambi.
   */
  sectionType?: GetTagSemanticiTipoSezione;
  /**
   * Se disabilitato, non verranno mostrate le colonne con i valori dei campi semantici
   * in modo automatico.
   */
  enabled: boolean;
}) {
  const { semanticFields, semanticTags, enabled, sectionType } = options;

  // Costruisco la lista dei tag semantici visualizzabili, prendendoli tra quelli
  // restituiti dalle entità in elenco
  // (e.g. prendo tutti i tag semantici tra i `campiSemantici` dei procedimenti, e di questi
  // filtro solo i visualizzabili).
  const visibleSemanticTags = useMemo(() => {
    if (!enabled) return [];
    const semanticTagList = semanticFields.map(s => s.tagSemantico);
    // Rimuovere i tag duplicati
    const semanticTagSet = uniqBy(semanticTagList, tag => tag?.id);

    // Filtrare per flVisibile
    // TODO -> Qui verrà sostituito con il flag adatto quando sarà disponibile (e.g. `flInTabella`)
    return (
      semanticTags.filter(tag => {
        return (
          semanticTagSet.findIndex(s => s?.id === tag?.id) !== -1 &&
          SemanticTagLogic.isVisible(tag)
        );
      }) ?? []
    );
  }, [enabled, semanticFields, semanticTags]);

  // Costruisco le colonne da visualizzare nella tabella
  const columns = useMemo(() => {
    if (!enabled) return [];
    return getSemanticTagColumns(visibleSemanticTags, sectionType);
  }, [sectionType, enabled, visibleSemanticTags]);

  return columns;
}
